import { useEffect, useState } from 'react';
import {
  Button,
  Select,
  Spinner,
  Tag,
  Toast,
  ToastBody,
  ToastTitle,
  Tooltip,
  makeStyles,
  useToastController,
} from '@fluentui/react-components';

import { AppTopBar } from '../components/AppTopBar.js';
import { Breadcrumbs } from '../components/Breadcrumbs.js';
import { UsageMeter } from '../components/UsageMeter.js';
import { useConfirm } from '../components/ConfirmDialog.js';
import { StaffTestingPanel } from '../components/StaffTestingPanel.js';
import { GLOBAL_TOASTER_ID } from '../components/ThemedProvider.js';
import { useAuthStore, useIsEcosoftStaff, useSubscriptionInfo } from '../stores/authStore.js';
import { useUsageStore } from '../stores/usageStore.js';
import {
  changeTenantMemberRole,
  listTenantMembers,
  removeTenantMember,
  type TenantMember,
  type TenantRole,
} from '../api/tenantMembers.js';
import { planShortLabel } from '../api/usage.js';
import { nombreProductoActivo } from '../lib/brandingDelProducto.js';

const ROL_LABEL: Record<TenantRole, string> = {
  owner: 'Propietario',
  admin: 'Administrador',
  member: 'Cotizador',
  viewer: 'Consulta (solo lectura + chat)',
};

const EDITABLE_ROLES: Array<'admin' | 'member' | 'viewer'> = ['admin', 'member', 'viewer'];

export function SettingsMembersPage() {
  const styles = useStyles();
  const confirm = useConfirm();
  const user = useAuthStore((s) => s.user);
  const tenant = useAuthStore((s) => s.tenant);
  const isAdmin = tenant?.role === 'admin' || tenant?.role === 'owner';

  const usage = useUsageStore((s) => s.data);
  const refreshUsage = useUsageStore((s) => s.refresh);
  const isEcosoftStaff = useIsEcosoftStaff();
  const { expired: subExpired } = useSubscriptionInfo();
  const plan = usage?.plan ?? 'start';
  const planLabel = planShortLabel(usage);
  const isTeams = plan === 'teams';

  const [members, setMembers] = useState<TenantMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);

  const { dispatchToast } = useToastController(GLOBAL_TOASTER_ID);

  useEffect(() => { void refreshUsage(); }, [refreshUsage]);

  useEffect(() => {
    if (!isAdmin) { setLoading(false); return; }
    setLoading(true);
    listTenantMembers()
      .then((res) => setMembers(res.members))
      .catch((err: Error) => {
        dispatchToast(
          <Toast><ToastTitle>No se pudieron cargar los miembros</ToastTitle><ToastBody>{err.message}</ToastBody></Toast>,
          { intent: 'error', timeout: 6000 },
        );
      })
      .finally(() => setLoading(false));
  }, [isAdmin, dispatchToast]);

  async function onChangeRole(member: TenantMember, role: 'admin' | 'member' | 'viewer') {
    if (role === member.role) return;
    setBusy(member.userId);
    try {
      await changeTenantMemberRole(member.userId, role);
      setMembers((prev) => prev.map((m) => (m.userId === member.userId ? { ...m, role } : m)));
      dispatchToast(
        <Toast><ToastTitle>{member.name || member.email} ahora es {ROL_LABEL[role]}</ToastTitle></Toast>,
        { intent: 'success', timeout: 3000 },
      );
    } catch (err) {
      dispatchToast(
        <Toast><ToastTitle>No se pudo actualizar</ToastTitle><ToastBody>{(err as Error).message}</ToastBody></Toast>,
        { intent: 'error', timeout: 6000 },
      );
    } finally {
      setBusy(null);
    }
  }

  async function onRemove(member: TenantMember) {
    const label = member.name || member.email;
    const ok = await confirm({
      title: 'Quitar de la empresa',
      message:
        `¿Quitar a ${label} de la empresa?\n\n` +
        'Si esta persona conserva sus credenciales corporativas, volverá a entrar como ' +
        'Cotizador la próxima vez que inicie sesión. Para bloqueo permanente, contacta al ' +
        'área que gestiona las licencias de tu organización.',
      confirmLabel: 'Quitar',
      destructive: true,
    });
    if (!ok) return;
    setBusy(member.userId);
    try {
      await removeTenantMember(member.userId);
      setMembers((prev) => prev.filter((m) => m.userId !== member.userId));
      dispatchToast(
        <Toast><ToastTitle>{label} fue retirado de la empresa</ToastTitle></Toast>,
        { intent: 'success', timeout: 3000 },
      );
    } catch (err) {
      dispatchToast(
        <Toast><ToastTitle>No se pudo remover</ToastTitle><ToastBody>{(err as Error).message}</ToastBody></Toast>,
        { intent: 'error', timeout: 6000 },
      );
    } finally {
      setBusy(null);
    }
  }

  if (!tenant) return null;

  const adminCount = members.filter((m) => m.role === 'admin' || m.role === 'owner').length;

  // ---- Panel izquierdo: equipo / cuenta según plan + rol ----
  let leftPanel: React.ReactNode;
  if (!isTeams) {
    // Plan individual (Start/Pro): tu cuenta, sin equipo.
    leftPanel = (
      <div className={styles.panel}>
        <div className={styles.eyebrow}>Tu cuenta</div>
        <p className={styles.panelDesc}>
          El plan <strong style={{ color: 'var(--text-primary)' }}>{planLabel}</strong> es individual:
          solo tú accedes a tus proyectos y catálogos. Para compartir con un equipo necesitas el plan Teams.
        </p>
        <div className={styles.accountRow}>
          <div className={styles.accountAvatar}>{(user?.name ?? user?.email ?? '?').slice(0, 1).toUpperCase()}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className={styles.accountName}>{user?.name ?? '(sin nombre)'}</div>
            <div className={styles.muted}>{user?.email}</div>
          </div>
          <span className={`${styles.badge} ${styles.badgeInfo}`}><span className={styles.badgeDot} />Plan {planLabel}</span>
        </div>
      </div>
    );
  } else if (!isAdmin) {
    // Teams sin rol admin.
    leftPanel = (
      <div className={styles.panel}>
        <div className={styles.eyebrow}>Miembros de {tenant.name}</div>
        <div className={styles.notice}>
          Necesitas rol de <strong style={{ color: 'var(--text-primary)' }}>Administrador</strong> para ver y gestionar
          el equipo. Tu rol actual es <strong style={{ color: 'var(--text-primary)' }}>{ROL_LABEL[tenant.role as TenantRole] ?? tenant.role}</strong>.
        </div>
      </div>
    );
  } else {
    // Teams + admin: tabla de miembros.
    leftPanel = (
      <div className={styles.panel}>
        <div className={styles.eyebrow}>Miembros de {tenant.name}</div>
        <p className={styles.panelDesc}>
          Se agregan automáticamente al iniciar sesión con credenciales corporativas. El <strong style={{ color: 'var(--text-primary)' }}>Propietario</strong> administra
          la suscripción, el <strong style={{ color: 'var(--text-primary)' }}>Administrador</strong> gestiona el equipo, el <strong style={{ color: 'var(--text-primary)' }}>Cotizador</strong> cotiza
          y <strong style={{ color: 'var(--text-primary)' }}>Consulta</strong> solo pregunta a la inteligencia artificial.
        </p>
        {loading ? (
          <div className={styles.empty}><Spinner label="Cargando miembros…" /></div>
        ) : members.length === 0 ? (
          <div className={styles.empty}>Esta empresa no tiene miembros todavía.</div>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Nombre</th>
                <th>Correo</th>
                <th>Rol</th>
                <th>Último acceso</th>
                <th aria-label="Acciones"></th>
              </tr>
            </thead>
            <tbody>
              {members.map((m) => {
                const isSelf = m.userId === user?.id;
                const isLocked = busy === m.userId;
                const isLastAdmin = (m.role === 'admin' || m.role === 'owner') && adminCount === 1;
                return (
                  <tr key={m.userId}>
                    <td>
                      <div className={styles.nameCell}>
                        <strong>{m.name || '(sin nombre)'}</strong>
                        {isSelf && <Tag size="extra-small">tú</Tag>}
                      </div>
                    </td>
                    <td className={styles.muted}>{m.email}</td>
                    <td>
                      {m.role === 'owner' ? (
                        <span className={`${styles.badge} ${styles.badgePrivado}`}><span className={styles.badgeDot} />Propietario</span>
                      ) : (
                        <Tooltip
                          content="Es el último administrador de la empresa. Asigna otro antes de cambiar este rol."
                          relationship="description"
                          visible={isLastAdmin && !isSelf ? undefined : false}
                        >
                          <Select
                            value={m.role}
                            disabled={isSelf || isLocked || isLastAdmin}
                            onChange={(_, d) => void onChangeRole(m, d.value as 'admin' | 'member' | 'viewer')}
                          >
                            {EDITABLE_ROLES.map((r) => (
                              <option key={r} value={r}>{ROL_LABEL[r]}</option>
                            ))}
                          </Select>
                        </Tooltip>
                      )}
                    </td>
                    <td className={styles.muted}>
                      {m.lastLoginAt
                        ? new Date(m.lastLoginAt).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' })
                        : '—'}
                    </td>
                    <td className={styles.right}>
                      {!isSelf && m.role !== 'owner' && (
                        <Tooltip content="Es el último administrador de la empresa." relationship="description" visible={isLastAdmin ? undefined : false}>
                          <Button size="small" appearance="subtle" disabled={isLocked || isLastAdmin} onClick={() => void onRemove(m)} style={{ color: 'var(--bad)' }}>
                            Quitar
                          </Button>
                        </Tooltip>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    );
  }

  // ---- Panel derecho: consumo del plan (datos actuales) ----
  const consumoPanel = (
    <div className={styles.panel}>
      <div className={styles.eyebrow}>Consumo del plan</div>
      <div className={styles.statusRow}>
        <span className={`${styles.badge} ${styles.badgeOficial}`} style={{ height: 26 }}><span className={styles.badgeDot} />Plan {planLabel}</span>
        <span className={`${styles.badge} ${subExpired ? styles.badgeBad : styles.badgeOk}`}>
          <span className={styles.badgeDot} />{subExpired ? 'Suscripción vencida' : 'Suscripción vigente'}
        </span>
      </div>
      <p className={styles.panelDesc} style={{ margin: '0 0 8px' }}>
        Suscripción a {nombreProductoActivo()}{isTeams ? ' · pool compartido por el equipo' : ' · individual'}.
      </p>
      {usage ? (
        <div className={styles.meters}>
          <UsageMeter label="Proyectos" current={usage.counters.projects_created} limit={usage.limits.projects_created} unit="proyectos" />
          <UsageMeter label="Conceptos cotizados" current={usage.counters.concepts_quoted} limit={usage.limits.concepts_quoted} unit="conceptos" />
          <UsageMeter label="APUs con inteligencia artificial" current={usage.counters.apus_generated} limit={usage.limits.apus_generated} unit="APUs" />
          <UsageMeter label="Chats" current={usage.counters.chats_sent} limit={usage.limits.chats_sent} unit="chats" />
          <UsageMeter label="Catálogos de referencia propios" current={usage.counters.catalogs_active} limit={usage.limits.catalogs_active} unit="catálogos" />
        </div>
      ) : (
        <Spinner size="tiny" label="Cargando consumo…" />
      )}
    </div>
  );

  return (
    <div className={styles.page}>
      <AppTopBar />
      <Breadcrumbs items={[{ label: 'Inicio', to: '/projects' }, { label: 'Administración' }]} />
      <div className={styles.container}>
        <div className={styles.pageHead}>
          <div>
            <h1 className={styles.title}>Administración</h1>
            <p className={styles.subtitle}>{isTeams ? `Equipo, roles y consumo del plan de ${tenant.name}.` : 'Tu cuenta y consumo del plan.'}</p>
          </div>
        </div>
        <div className={styles.split}>
          <section>{leftPanel}</section>
          <aside>{consumoPanel}</aside>
        </div>

        {/* Herramientas de prueba — SOLO staff Ecosoft (admin/owner del tenant Ecosoft) */}
        {isEcosoftStaff && (
          <div style={{ marginTop: 16 }}>
            <StaffTestingPanel />
          </div>
        )}
      </div>
    </div>
  );
}

const useStyles = makeStyles({
  page: { minHeight: '100vh', background: 'var(--bg-page)' },
  container: { maxWidth: '1180px', margin: '0 auto', padding: '24px 28px' },
  pageHead: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '20px', marginBottom: '20px' },
  title: { margin: 0, fontFamily: 'var(--font-display)', fontSize: '23px', fontWeight: 700, letterSpacing: '-0.3px', lineHeight: 1.2 },
  subtitle: { margin: '5px 0 0', color: 'var(--text-secondary)', fontSize: '13px', lineHeight: 1.5 },
  split: {
    display: 'grid',
    gridTemplateColumns: '1fr 360px',
    gap: '16px',
    alignItems: 'start',
    '@media (max-width: 980px)': { gridTemplateColumns: '1fr' },
  },
  panel: {
    background: 'var(--bg-surface)',
    border: '1px solid var(--border-subtle)',
    borderRadius: 'var(--radius-lg)',
    padding: '18px 20px',
  },
  eyebrow: { fontSize: '11px', fontWeight: 700, letterSpacing: '1.1px', textTransform: 'uppercase', color: 'var(--text-tertiary)' },
  panelDesc: { fontSize: '12.5px', color: 'var(--text-secondary)', margin: '6px 0 14px', lineHeight: 1.45 },
  notice: {
    marginTop: '12px',
    border: '1px solid var(--border-strong)',
    background: 'var(--bg-raised)',
    borderRadius: 'var(--radius-md)',
    padding: '11px 13px',
    fontSize: '12.5px',
    color: 'var(--text-secondary)',
    lineHeight: 1.45,
  },
  empty: { padding: '28px', textAlign: 'center', color: 'var(--text-tertiary)' },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    '& th, & td': {
      padding: '10px 12px',
      textAlign: 'left',
      verticalAlign: 'middle',
      borderBottom: '1px dashed var(--border-subtle)',
      fontSize: '12.5px',
    },
    '& thead th': {
      fontSize: '10.5px',
      fontWeight: 650,
      textTransform: 'uppercase',
      letterSpacing: '0.06em',
      color: 'var(--text-tertiary)',
      borderBottom: '1px solid var(--border-subtle)',
    },
    '& tbody tr:last-child td': { borderBottom: 'none' },
  },
  nameCell: { display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 600 },
  right: { textAlign: 'right' },
  muted: { color: 'var(--text-secondary)' },
  accountRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    border: '1px solid var(--border-subtle)',
    borderRadius: 'var(--radius-md)',
    background: 'var(--bg-raised)',
    padding: '12px 14px',
  },
  accountAvatar: {
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    background: 'var(--accent-weak)',
    color: 'var(--accent)',
    display: 'grid',
    placeItems: 'center',
    fontWeight: 800,
    fontSize: '15px',
    flexShrink: 0,
  },
  accountName: { fontSize: '13.5px', fontWeight: 650, color: 'var(--text-primary)' },
  statusRow: { display: 'flex', alignItems: 'center', gap: '9px', flexWrap: 'wrap', margin: '10px 0 8px' },
  meters: { display: 'flex', flexDirection: 'column', gap: '2px' },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    height: '23px',
    padding: '0 9px',
    borderRadius: '999px',
    fontSize: '11.5px',
    fontWeight: 650,
    whiteSpace: 'nowrap',
  },
  badgeDot: { width: '7px', height: '7px', borderRadius: '50%', background: 'currentColor', flexShrink: 0 },
  badgeInfo: { background: 'var(--accent-weak)', color: 'var(--accent)' },
  badgeOficial: { background: 'var(--oficial-weak)', color: 'var(--oficial)' },
  badgePrivado: { background: 'var(--privado-weak)', color: 'var(--privado)' },
  badgeOk: { background: 'var(--ok-weak)', color: 'var(--ok)' },
  badgeBad: { background: 'var(--bad-weak)', color: 'var(--bad)' },
});
